import React, { useState, useEffect, createContext, useContext } from 'react';
import { Outlet, Navigate } from 'react-router-dom';
import { $api } from './api';
import Sidebar from './components/Layout/Sidebar';
import Header from './components/Layout/Header';
import SandboxBanner from './components/Layout/SandboxBanner';
import { User } from './types';
import { useOrganization } from './contexts/OrganizationContext';

// Context type for authentication
export interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  handleLogin: () => void;
  handleLogout: () => void;
}

const AuthContext = createContext<AuthContextType>({
  isAuthenticated: false,
  user: null,
  handleLogin: () => {},
  handleLogout: () => {},
});

// Hook to use auth context
export const useAuth = () => {
  return useContext(AuthContext)
};

// Root component that manages authentication
export const RootLayout: React.FC = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const hasToken = !!localStorage.getItem('auth_token');

  // Fetch user profile with React Query, enabled only when there's a token
  const profileQuery = $api.useQuery('get', '/api/settings/profile', {
    queryKey: ['profile'],
    enabled: hasToken,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Handle profile data and authentication state
  useEffect(() => {
    if (profileQuery.data) {
      setUser({
        role: 'admin',
        name: `${profileQuery.data.first_name} ${profileQuery.data.last_name}`,
        email: profileQuery.data.email,
      });
      setIsAuthenticated(true);
      setIsLoading(false);
    } else if (profileQuery.isError) {
      console.error('[Auth] Token validation error:', profileQuery.error);
      localStorage.removeItem('auth_token');
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
    } else if(!profileQuery.isLoading && !profileQuery.data) {
      setIsAuthenticated(false);
      setUser(null);
      setIsLoading(false);
    }
  }, [profileQuery.data, profileQuery.isError, profileQuery.error, profileQuery.isLoading]);

  const handleLogin = async () => {
    setIsAuthenticated(true);
    await profileQuery.refetch();
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUser(null);
    localStorage.removeItem('auth_token');
  };

  // Show loading screen while checking authentication
  if (hasToken && isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={{ isAuthenticated, user, handleLogin, handleLogout }}><Outlet /></AuthContext.Provider>
};

// Protected route wrapper
export const ProtectedRoute: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated } = useAuth();
  
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  return children ?? <Outlet />;
};

export const ProtectedOrganizationRoute: React.FC<{ children?: React.ReactNode }> = ({ children }) => {
  const { isLoading, currentOrganization, organizations } = useOrganization();
  if(!isLoading && !currentOrganization && organizations.length == 0) {
    return <Navigate to="/organizations/create" replace />;
  }

  return children ?? <Outlet />;
};

// Main layout with sidebar and header for protected routes
export const MainLayout: React.FC = () => {
  const { user, handleLogout } = useAuth();
  
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar onLogout={handleLogout} />
      
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header user={user} />
        <SandboxBanner />
        
        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
};